<template>
  <form class="p2a-action-form">
    <!-- Honeypot Field - Zip Code -->
    <field-text
      v-show="false"
      id="input-zipcode"
      :name="formHelper.getFieldName(formFields.zipcode)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.zipcode)"
      :value="formHelper.getFieldValue(formFields.zipcode)"
      autocomplete="off"
      @blur="blurField(formFields.zipcode, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.zipcode, $event.target.value)"
    />

    <!-- Honeypot Field - Address -->
    <field-text
      v-show="false"
      :name="formHelper.getFieldName(formFields.address)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.address)"
      :value="formHelper.getFieldValue(formFields.address)"
      autocomplete="off"
      @blur="blurField(formFields.address, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.address, $event.target.value)"
    />

    <field-select
      v-if="formHelper.isFieldEnabled(formFields.title)"
      :required="formHelper.isFieldRequired(formFields.title)"
      :default-empty-option="true"
      :name="formHelper.getFieldName(formFields.title)"
      :options="formHelper.getFieldProp(formFields.title, 'options', [])"
      :default-empty-label="formHelper.getFieldPlaceholder(formFields.title)"
      :value="formHelper.getFieldValue(formFields.title)"
      :error="validationErrors.title"
      autocomplete-props="honorific-prefix"
      @blur="blurField(formFields.title, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.title, $event.target.value)"
    />

    <field-text
      v-if="formHelper.isFieldEnabled(formFields.fullName)"
      :required="formHelper.isFieldRequired(formFields.fullName)"
      :name="formHelper.getFieldName(formFields.fullName)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.fullName)"
      :value="formHelper.getFieldValue(formFields.fullName)"
      :error="validationErrors.fullName"
      autocomplete="name"
      @blur="blurField(formFields.fullName, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.fullName, $event.target.value)"
    />

    <field-text
      v-if="formFields.address1"
      ref="address1"
      :required="formHelper.isFieldRequired(formFields.address1)"
      :name="formHelper.getFieldName(formFields.address1)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.address1)"
      :value="formHelper.getFieldValue(formFields.address1)"
      :error="validationErrors.address1"
      @blur="blurField(formFields.address1, $event, true)"
      @input="formHelper.setFormFieldValueInStore(formFields.address1, $event.target.value)"
    />

    <field-text
      v-if="formFields.zip5"
      :required="formHelper.isFieldRequired(formFields.zip5)"
      :name="formHelper.getFieldName(formFields.zip5)"
      :placeholder="formHelper.getZipFieldPlaceholder(formHelper.getFieldPlaceholder(formFields.zip5))"
      :value="formHelper.getFieldValue(formFields.zip5)"
      :error="formHelper.getZipFieldValidationError(validationErrors.zip5)"
      @blur="blurField(formFields.zip5, $event, true)"
      @input="formHelper.setFormFieldValueInStore(formFields.zip5, $event.target.value)"
    />

    <field-text
      v-if="formHelper.isFieldEnabled(formFields.phone)"
      :required="formHelper.isFieldRequired(formFields.phone)"
      :name="formHelper.getFieldName(formFields.phone)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.phone)"
      :value="formHelper.getFieldValue(formFields.phone)"
      :error="validationErrors.phone"
      type="tel"
      autocomplete="tel"
      @blur="blurField(formFields.phone, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.phone, $event.target.value)"
    />

    <field-text
      v-if="formHelper.isFieldEnabled(formFields.email)"
      :required="formHelper.isFieldRequired(formFields.email)"
      :name="formHelper.getFieldName(formFields.email)"
      :placeholder="formHelper.getFieldPlaceholder(formFields.email)"
      :value="formHelper.getFieldValue(formFields.email)"
      :error="validationErrors.email"
      type="email"
      autocomplete="email"
      @blur="blurField(formFields.email, $event)"
      @input="formHelper.setFormFieldValueInStore(formFields.email, $event.target.value)"
    />

    <!-- Custom Fields -->
    <fieldset
      v-for="field in customFields"
      :key="formHelper.getFieldName(field)"
      :class="['p2a-fieldset-custom-field', field.inputType, {
        'p2a-requires-input': formHelper.isFieldRequired(field),
        'p2a-input-error': validationErrors[field.name]
      }]"
    >
      <legend v-if="!formHelper.isSingleOptionCheckbox(field)">{{ field.label }}</legend>

      <field-text
        v-if="formHelper.isInput(field)"
        :id="'custom-field-id-' + field.id"
        :name="formHelper.getFieldName(field)"
        :value="formHelper.getFieldValue(field)"
        :required="formHelper.isFieldRequired(field)"
        autocomplete="turned-off"
        class="p2a-input-label"
        @blur="blurField(field, $event)"
        @input="formHelper.setFormFieldValueInStore(field, $event.target.value)"
      />

      <template v-if="formHelper.isCheckbox(field)">
        <field-checkbox
          v-for="(checkboxOption, index) in formHelper.transformCustomFieldOptions(field)"
          :key="index"
          :id="checkboxOption.label"
          :label="checkboxOption.label"
          :field="field"
          @checked="handleCheckBox"
        />
      </template>

      <template v-if="formHelper.isRadio(field)">
        <field-radio
          v-for="(radioOption, index) in formHelper.transformCustomFieldOptions(field)"
          :key="index"
          :id="radioOption.label"
          :name="formHelper.getFieldName(field)"
          :label="radioOption.label"
          :field="field"
          :value="radioOption.value"
          @selected="handleFormFieldValue(field, radioOption.value)"
        />
      </template>

      <field-select
        v-if="formHelper.isSelect(field)"
        :id="'custom-field-id-' + field.id"
        :name="formHelper.getFieldName(field)"
        :default-empty-option="true"
        :options="formHelper.transformCustomFieldOptions(field)"
        :value="formHelper.getFieldValue(field)"
        :required="formHelper.isFieldRequired(field)"
        autocomplete-props="turned-off"
        class="p2a-styled-select"
        @blur="blurField(field, $event)"
        @input="formHelper.setFormFieldValueInStore(field, $event.target.value)"
      />

      <field-text
        v-if="formHelper.isTextArea(field)"
        :id="'custom-field-id-' + field.id"
        :name="formHelper.getFieldName(field)"
        :value="formHelper.getFieldValue(field)"
        :required="formHelper.isFieldRequired(field)"
        type="textarea"
        autocomplete="turned-off"
        class="p2a-input-label"
        @blur="blurField(field, $event)"
        @input="formHelper.setFormFieldValueInStore(field, $event.target.value)"
      />

      <span
        v-show="validationErrors[field.name]"
        class="p2a-error-message"
        role="alert"
      >{{ validationErrors[field.name] }}</span>
    </fieldset>

    <!-- ajax errors -->
    <p
      v-if="ajaxError"
      class="p2a-error-message"
    >
      {{ ajaxError }}
    </p>

    <primary-button
      :disabled="stakeholderError"
      @click.native="submitForm"
    >
      <icon
        v-if="loading"
        class="spinner"
        icon-name="loader-fa"
        view-box="0 0 28 28"
      />
      <span v-else>{{ submitText }}</span>
    </primary-button>

    <!-- form errors -->
    <p
      v-if="error && !isFormValid"
      class="p2a-error-message"
    >
      {{ error }}
    </p>
  </form>
</template>
<script>
import { mapActions, mapState } from "vuex";
import { isEmpty as _isEmpty } from "lodash";
import Icon from "StyleguideComponents/Icon.vue";
import Routes from "Constants/campaign/whodoyouknow/routeNames";
import FieldCheckbox from "WhoDoYouKnowComponents/fields/FieldCheckbox.vue";
import FieldRadio from "WhoDoYouKnowComponents/fields/FieldRadio.vue";
import FieldText from "WhoDoYouKnowComponents/fields/FieldText.vue";
import FieldSelect from "WhoDoYouKnowComponents/fields/FieldSelect.vue";
import PrimaryButton from "WhoDoYouKnowComponents/buttons/PrimaryButton.vue";
import VueValidatorHelper from "JS/helpers/vueValidator";
import AutocompleteFieldsOption from "JS/helpers/addressAutocomplete/fieldsOption";

export default {
  name: "LookupForm",
  components: {
    FieldCheckbox,
    FieldRadio,
    FieldText,
    FieldSelect,
    Icon,
    PrimaryButton
  },
  inject: {
    $validator: { from: "$validator" },
    axiosHelper: { from: "axiosHelper" },
    stakeholderService: { from: "stakeholderService" },
    advocateService: { from: "advocateService" },
    formHelper: { from: "formHelper" }
  },
  data() {
    return {
      selectedAddress: {
        address1: null,
        zip5: null,
      }
    }
  },
  computed: {
    ...mapState("campaign", {
      formFields: state =>
        state.formFields.default ? state.formFields.default.welcome : {},
      formErrorMessage: state => state.invalidFormFieldsErrorMessage,
      ajaxErrorMessage: state => state.unknownErrorMessage
    }),
    ...mapState("wdyk", {
      isMobile: state => state.isMobile,
      loading: state => state.loading,
      stakeholderError: state => state.stakeholderError
    }),
    ...mapState("forms", {
      addressAutocomplete: state => state.addressAutocomplete,
      error: state => state.error,
      formValues: state => state.formValues,
      honeypotPageLoadTime: state => state.honeypotPageLoadTime,
      validationResults: state => state.validationResults
    }),
    ajaxError() {
      return !_isEmpty(this.error) && this.isFormValid
        ? this.error
        : this.stakeholderErrorMessage;
    },
    customFields() {
      return this.formHelper.getCustomFields(this.formFields);
    },
    isFormValid() {
      return this.formHelper.isFormValid(this.formFields);
    },
    stakeholderErrorMessage() {
      return this.stakeholderError ? this.ajaxErrorMessage : null;
    },
    submitText() {
      return !this.loading ? "Next" : "Loading...";
    },
    validationErrors() {
      return this.validationResults.default || {};
    }
  },
  created() {
    this.initFormValidation();
    this.formHelper.initCountry(this.formFields);
  },
  mounted() {
    this.initAddressAutocomplete();
    this.addAutoCompleteListeners();
  },
  methods: {
    ...mapActions({
      setLoading: "wdyk/loading",
      setAddressAutocomplete: "forms/addressAutocomplete",
      setFormError: "forms/error"
    }),
    async blurField(field, $event, isManual = false) {
      await this.handleFormFieldValue(field, $event.target.value, isManual);
    },
    addAutoCompleteListeners() {
      // listen for fields to be auto-filled
      // (needed for Firefox since the blur event is not fired when field is auto-filled)
      if (this.formHelper.isFirefox()) {
        Object.keys(this.formFields).forEach(field => {
          const fieldId = `input-${field}`;
          const input = document.getElementById(fieldId);

          if (input) {
            if (field === "address1") {
              setTimeout(() => {
                input.setAttribute("autocomplete", "street-address");
              }, 1000);
            }

            this.formHelper
              .registerOnAutoComplete(`#${fieldId}`)
              .then(element => {
                this.handleFormFieldValue(
                  this.formFields[field],
                  element.value
                );
              });
          }
        });
      }
    },
    handleCheckBox(field, value, isChecked) {
      const fieldValues = this.formHelper.updateCheckboxValue(
        field,
        value,
        isChecked
      );

      this.handleFormFieldValue(field, fieldValues);
    },
    async handleFormValidation() {
      const formFields = Object.values(this.formFields);

      await Promise.all(
        formFields.map(field =>
          this.handleFormFieldValue(field, this.formHelper.getFieldValue(field))
        )
      );
    },
    async handleFormFieldValue(field, value, isManual = false) {
      try {
        const validationResults = await this.validatorHelper.isFieldValid(
          field.name,
          value
        );
        const validationErrors = this.formHelper.getValidationErrorMessage(
          validationResults
        );

        this.formHelper.setValidationResultsInStore(field, validationErrors);
        this.formHelper.setFormFieldValueInStore(field, value);
        this.formHelper.toggleTitleDefaultSelectedState(field, value);

        // remove error message if all fields are now valid
        if (this.isFormValid) {
          this.setFormError(null);
        }
        
        if (isManual) {
          this.detectedManualChange(field.name, value);
        }

        if (field.name === "country") {
          this.initAddressAutocomplete(field.name, value);
        }

        // We want to pre-fetch stakeholders if we get an address and/or zip
        if (this.formHelper.addressHasChanged(field, validationResults)) {
          this.prefetchStakeholders();
        }
      } catch (err) {
        this.formHelper.setFormFieldValueInStore(field, value);
      }
    },
    detectedManualChange(fieldName, fieldValue) {
      const keys = ['zip5', 'address1', 'country'];
      const isInputAddressField = keys.includes(fieldName);
      const isGeocodedAddress = this.formHelper.getFieldValue(this.formFields.lat) !== null && this.formHelper.getFieldValue(this.formFields.lon) !== null;
      const selectedAddress = this.formHelper.getSelectedAddress();
      const isAddressChanged = selectedAddress[fieldName] !== null && selectedAddress[fieldName] !== fieldValue;
      
      if (isInputAddressField && isGeocodedAddress && isAddressChanged) {
        this.purgeGeocodedAddressData(fieldName === 'country');
      }
    },
    purgeGeocodedAddressData() {
      this.formHelper.setFormFieldValueInStore(this.formFields.lat, '');
      this.formHelper.setFormFieldValueInStore(this.formFields.lon, '');
      this.formHelper.setFormFieldValueInStore(this.formFields.state, '');
      this.formHelper.setFormFieldValueInStore(this.formFields.city, '');
      this.formHelper.setFormFieldValueInStore(this.formFields.county, '');
      this.formHelper.setFormFieldValueInStore(this.formFields.zip4, '');
    },
    initAddressAutocomplete() {
      const autocompleteSettings = {
        fields: new AutocompleteFieldsOption(),
        types: ["address"],
        componentRestrictions: { country: this.formHelper.getCountryValue() }
      };

      // initialize Google address autocomplete on Address Street 1 field
      if (!_isEmpty(this.formFields.address1) && this.$refs.address1) {
        const field = this.$refs.address1;
        const input = field.$el.querySelector("input");

        // eslint-disable-next-line no-undef
        const googleAddress = new google.maps.places.Autocomplete(
          input,
          autocompleteSettings
        );

        this.setAddressAutocomplete(googleAddress);
        this.addressAutocomplete.addListener("place_changed", () => {
          this.formHelper.addressAutocompleteSelected();
          this.handleFormFieldValue(
            this.formFields.address1,
            this.formValues.address1
          );
        });
      }
    },
    initFormValidation() {
      this.validatorHelper = new VueValidatorHelper(this.formFields);
      this.validatorHelper.setValidator(this.$validator);
      this.formHelper.addZipValidationRule();
    },
    prefetchStakeholders() {
      const address = this.advocateService.getAddress(this.formValues);

      this.stakeholderService.findByAddress(
        address
      );
    },
    async submitForm() {
      this.setFormError("");
      await this.handleFormValidation();

      const formValid = this.formHelper.isFormValid(this.formFields);

      if (!formValid) {
        const error = this.formErrorMessage;
        this.setFormError(error);
        this.setLoading(false);

        return false;
      }

      try {
        this.setLoading(true);
        await this.advocateService.createAdvocate(
          this.formValues,
          this.customFields,
          this.honeypotPageLoadTime
        );

        this.setLoading(false);
        this.$router.push({ name: Routes.STAKEHOLDER_RESULTS });
      } catch (error) {
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);

        this.setFormError(errorMessage);
        this.setLoading(false);
      }

      return true;
    }
  }
};
</script>
